import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { MatTableDataSource } from '@angular/material';
import { SelectionModel } from '@angular/cdk/collections';
import { TranslationService } from '../services/translation.service';
import { AlignResponsesService } from '../services/align-responses.service';
import { AlertService } from '../alerts/alert.service';
import { QuestBlockModalComponent } from './partials/quest-block-modal.component';
import { AlignResponsesEditModalComponent } from './partials/align-responses-edit-modal.component';
import { ConfirmDeleteAlignResponseModalComponent } from './partials/confirm-delete-align-response-modal.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { PermissionsService } from '../services/permissions.service';

@Component({
  selector: "app-align-responses",
  templateUrl: "align-responses.component.html",
})
export class AlignResponsesComponent implements OnInit, OnDestroy {
  private sub: any;
  public questBlockKey: string;
  public translations: any = {};
  public permissions = [];
  bsModalRef: BsModalRef;
  bsConfig: Partial<BsDatepickerConfig>;
  displayedColumns = [
    "Student",
    "QuestBlockKey",
    "Age",
    "ProcessedTime",
    "Change",
    "Confirm",
  ];
  unalignDisplayedColumn = [
    "select",
    "Student",
    "QuestBlockKey",
    "Age",
    "ProcessedTime",
    "Change",
    "Confirm",
    "Delete",
  ];
  alignedDataSource = new MatTableDataSource();
  unAlignedDataSource = new MatTableDataSource();
  alignedSelection = new SelectionModel(true, []);
  unalignedSelection = new SelectionModel(true, []);
  loading = true;

  public get isSelectedAllUnalignResponse(): boolean {
    return (
      this.unAlignedDataSource.data.length ===
        this.unalignedSelection.selected.length &&
      !!this.unalignedSelection.selected.length
    );
  }

  public get selectedSubmissionInfo() {
    return this.unalignedSelection.selected.map(
      (item) => item.submission.student_quest_block_submission_match.FullName
    );
  }

  public get selectedSubmissionIDs(): number[] {
    return this.unalignedSelection.selected.map(
      (item) => item.submission.SubmissionId
    );
  }

  public get isSelected(): boolean {
    return !!this.unalignedSelection.selected.length
  }

  constructor(
    private modalService: BsModalService,
    private route: ActivatedRoute,
    private translationService: TranslationService,
    private alignResponsesService: AlignResponsesService,
    private alertService: AlertService,
    private permissionService: PermissionsService
  ) {
    this.translations = translationService.getTranslations();
    this.permissions = this.permissionService.getPermissions();
    this.modalService.onHide.subscribe(() => {
      /**
       * Ignore unnecessary refresh if the user cancels the delete action.
       * This helps prevent loss of selection, as refreshing will re-render the entire table
       */
      const shouldRefresh =
        this.bsModalRef.content.confirmModalName ===
        "confirm-delete-align-response-modal"
          ? this.bsModalRef.content.shouldRefresh
            ? true
            : false
          : true;
      if (shouldRefresh) {
        this.refresh();
        this.unalignedSelection.clear();
      }
    }); // refresh on close modal
  }

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      this.questBlockKey = params["key"];
    });

    this.loading = true;
    this.refresh();
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  refresh() {
    this.alignResponsesService
      .getSubmissionsByQuestBlock(this.questBlockKey)
      .then((questBlocks) => {
        this.alignedDataSource = new MatTableDataSource(questBlocks.aligned);
        this.unAlignedDataSource = new MatTableDataSource(
          questBlocks.unaligned
        );
        this.loading = false;
      })
      .catch((msg) => {
        console.log(msg);
        this.alertService.show(msg.error.message, "danger");
        this.loading = false;
        window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
      });
  }

  confirmMultipleUnaligned() {
    if(!this.isSelected) return;
    (this.isSelectedAllUnalignResponse && this.selectedSubmissionIDs.length >1
      ? this.alignResponsesService.confirmAllUnaligned(this.questBlockKey)
      : this.alignResponsesService.confirmMultipleUnaligned(
          this.questBlockKey,
          this.selectedSubmissionIDs
        )
    )
      .then((data) => {
        this.alertService.show(
          `Successfully aligned ${this.isSelectedAllUnalignResponse?'all' :'multiple'} unaligned for this quest block that have matches. It will take up to 15 mins to appear in Child Scores while we calculate their scores.`,
          "success"
        );
        this.refresh();
        this.unalignedSelection.clear();
      })
      .catch((msg) => {
        console.log(msg);
        this.alertService.show(msg.error.message, "danger");
        window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
      });
  }

  confirmResponse(event: Event, element) {
    event.stopPropagation();
    this.alignResponsesService
      .confirmMultipleUnaligned(this.questBlockKey, [
        element.submission.SubmissionId,
      ])
      .then((data) => {
        this.alertService.show(
          "Successfully aligned individual unaligned for this quest block that have matches. It will take up to 15 mins to appear in Child Scores while we calculate their scores.",
          "success"
        );
        this.refresh();
        this.unalignedSelection.clear();
      })
      .catch((msg) => {
        console.log(msg);
        this.alertService.show(msg.error.message, "danger");
        window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
      });
  }

  applyAlignedFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
    this.alignedDataSource.filter = filterValue;
  }

  applyUnalignedFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
    this.unAlignedDataSource.filter = filterValue;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelectedAligned() {
    const numSelected = this.alignedSelection.selected.length;
    const numRows = this.alignedDataSource.data.length;
    return numSelected === numRows;
  }
  isAllSelectedUnaligned() {
    const numSelected = this.unalignedSelection.selected.length;
    const numRows = this.unAlignedDataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggleAligned() {
    this.isAllSelectedAligned()
      ? this.alignedSelection.clear()
      : this.alignedDataSource.data.forEach((row) =>
          this.alignedSelection.select(row)
        );
  }
  masterToggleUnaligned() {
    this.isAllSelectedUnaligned()
      ? this.unalignedSelection.clear()
      : this.unAlignedDataSource.data.forEach((row) =>
          this.unalignedSelection.select(row)
        );
  }

  openConfirmDeleteDialog(event: Event, element) {
    event.stopPropagation(); //
    const componentType = ConfirmDeleteAlignResponseModalComponent;
    const initialState: any = {
      title: "DELETE UNALIGNED",
      data: {
        selectedSubmissionIDs: [element.submission.SubmissionId],
        selectedSubmissionInfo: [
          element.submission.student_quest_block_submission_match.FullName,
        ],
        isDeleteAll: false,
        questBlockKey: this.questBlockKey,
      },
    };

    this.bsModalRef = this.modalService.show(componentType, {
      initialState,
      class: "modal-lg",
    });
    this.bsModalRef.content.closeBtnName = "Close";
  }

  openConfirmMultipleDeleteDialog() {
    if(!this.isSelected) return;
    const componentType = ConfirmDeleteAlignResponseModalComponent;
    const initialState: any = {
      title: "DELETE UNALIGNED",
      data: {
        selectedSubmissionIDs: this.selectedSubmissionIDs,
        selectedSubmissionInfo: this.selectedSubmissionInfo,
        isDeleteAll: this.isSelectedAllUnalignResponse,
        questBlockKey: this.questBlockKey,
      },
    };

    this.bsModalRef = this.modalService.show(componentType, {
      initialState,
      class: "modal-lg",
    });
    this.bsModalRef.content.closeBtnName = "Close";
  }

  openDialog(event: Event, alignResponsesBlockObject: object, type: string) {
    event.stopPropagation();
    const componentType = AlignResponsesEditModalComponent;
    const initialState: any = {
      data: {
        alignResponsesBlockObject: {},
      },
      type: type,
      title: "Manage Response",
    };

    if (type === "edit") {
      initialState.title = "Edit Response";
      initialState.data.alignResponsesBlockObject = alignResponsesBlockObject;
      initialState.data.quest_block_key = this.questBlockKey
    } else if (type === "add") {
      initialState.title = "Add Response";
    }

    this.bsModalRef = this.modalService.show(componentType, {
      initialState,
      class: "modal-lg",
    });
    this.bsModalRef.content.closeBtnName = "Close";
  }
}
